import { useState, useRef, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Avatar,
  Dialog,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Public,
  Lock,
  Language,
  ArrowLeft,
  ArrowRight,
} from "@mui/icons-material";
import { ChannelService } from "../../services/channelService";
import { ChannelMemberDto } from "../../types/channelMember";
import { ProfileDto } from "../../types/UserToken";
import {
  onReceiveChannelMessage,
  onReceiveGlobalMessage,
  onReceiveVenomMessage,
  sendChannelMessage,
  sendGlobalMessage,
} from "../../services/signalRService";
import { useSignalRConnection } from "../../hooks/connectionSignalR";
import { ChannelMessageDto } from "../../types/channelMessage";
import { GlobalMessageDto } from "../../types/globalMessage"; //importe el tipo GlobalMessageDto
import { FilesDto, NormalizedMessage } from "../../types/normalizedMessage"; //Normaliza los mensajes para canal y global
import { ChannelDto } from "../../types/channel";
import EditChannel from "./EditChannel";
import { useNavigate } from "react-router/dist";
import ChannelDetailsDialog from "./dialogs/ChannelDetailsDialog";
import { MessageInput } from "./MessageInput";
import { MembersDialog } from "./dialogs/MembersDialog";
import { HeaderMessageBody } from "./HeaderMessageBody";
import { MessageActionsMenu } from "./MessageActionsMenu";

interface MessageAreaProps {
  messages: ChannelMessageDto[];
  channel: ChannelDto | null;
  profiles?: Record<number, ProfileDto>;
  members: ChannelMemberDto[];
  currentUser: ProfileDto;
  isGlobal: boolean;
  handleShowChats: () => void;
  fetchChannels?: () => Promise<void>;
  fetchPublicChannels?: () => Promise<void>;
  setSelectedTab?: (tab: "channels" | "global") => void;
  setSelectedChannel?: (channel: ChannelDto | null) => void;
  activeUserIds?: number[];
}

export default function MessageArea({
  channel,
  members,
  currentUser,
  isGlobal,
  handleShowChats,
  fetchChannels,
  fetchPublicChannels,
  setSelectedTab,
  setSelectedChannel,
  activeUserIds,
}: MessageAreaProps) {
  const [newMessage, setNewMessage] = useState("");
  const [membersDialogOpen, setMembersDialogOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedMember, setSelectedMember] = useState<ChannelMemberDto | null>(
    null
  );
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [channelMembers, setChannelMembers] =
    useState<ChannelMemberDto[]>(members);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<ProfileDto[]>([]);
  const [liveMessages, setLiveMessages] = useState<NormalizedMessage[]>([]);
  const [initialMessages, setInitialMessages] = useState<NormalizedMessage[]>(
    []
  );
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [channelInfo, setChannelInfo] = useState(channel);
  const [openChannelDetails, setOpenChannelDetails] = useState(false);
  const [filter, setFilter] = useState("today");
  const [files, setFiles] = useState<File[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [activePreviewFiles, setActivePreviewFiles] = useState<FilesDto[]>([]);
  const [activePreviewFileIndex, setActivePreviewFileIndex] = useState(0);
  const navigate = useNavigate();

  const allMessages = useMemo(() => {
    if (!isGlobal) {
      return [...initialMessages, ...liveMessages];
    }

    const now = new Date();
    const messages = [...initialMessages, ...liveMessages];

    return messages.filter((msg) => {
      const sentAt = new Date(msg.sentAt);

      switch (filter) {
        case "today":
          return sentAt.toDateString() === now.toDateString();
        case "yesterday":
          const yesterday = new Date(now);
          yesterday.setDate(now.getDate() - 1);
          return sentAt.toDateString() === yesterday.toDateString();
        case "week":
          const weekAgo = new Date(now);
          weekAgo.setDate(now.getDate() - 7);
          return sentAt >= weekAgo;
        case "month":
          const monthAgo = new Date(now);
          monthAgo.setMonth(now.getMonth() - 1);
          return sentAt >= monthAgo;
        case "all":
        default:
          return true;
      }
    });
  }, [initialMessages, liveMessages, filter, isGlobal]);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "warning" | "info";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const currentUserMember = channelMembers.find(
    (member) => member.profileId === currentUser.id
  );
  const isAdmin = currentUserMember?.role === "Admin";

  useEffect(() => {
    if (isGlobal) return;
    scrollToBottom();
  }, [allMessages, isGlobal]);

  useEffect(() => {
    setChannelInfo(channel);
  }, [channel]);

  useEffect(() => {
    setLiveMessages([]);
    setInitialMessages([]);
    setNewMessage("");
    setFiles([]);
  }, [channel?.id, isGlobal]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTop = 0;
    }
  }, [filter]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const connected = useSignalRConnection(currentUser?.id.toString());

  const handleSendMessage = async () => {
    if (!currentUser.id) {
      setSnackbar({
        open: true,
        message: "Debes iniciar sesión para enviar mensajes",
        severity: "error",
      });
      navigate("/properties/home");
      return;
    }

    if (!newMessage.trim()) return;
    if (!connected) {
      console.error("SignalR no está conectado aún");
      return;
    }
    setIsLoading(true);
    let success = false;

    if (channel?.id && !isGlobal) {
      success = await sendChannelMessage(
        channel.id,
        currentUser.id,
        newMessage.trim(),
        files.length > 0 ? "archivo" : "texto",
        files
      );
    } else if (isGlobal) {
      success = await sendGlobalMessage(
        newMessage.trim(),
        currentUser.id,
        "global",
        files
      );
    } else {
      return;
    }
    if (!success) {
      console.error("No se pudo enviar el mensaje");
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
    setNewMessage("");
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    } else {
      return date.toLocaleDateString([], { month: "short", day: "numeric" });
    }
  };

  const getChannelIcon = () => {
    if (isGlobal) return <Language sx={{ color: "#308a46" }} />;
    if (!channel) return null;
    return channel.isPublic ? (
      <Public
        sx={{ color: "success.main", fontSize: { xs: "20px", md: "24px" } }}
      />
    ) : (
      <Lock
        sx={{ color: "warning.main", fontSize: { xs: "20px", md: "24px" } }}
      />
    );
  };

  const getChannelTitle = () => {
    if (!channelInfo) return "Selecciona un canal";
    if (channelInfo.name.length > 15) {
      return `# ${channelInfo.name.substring(0, 15)}...`;
    }
    return `# ${channelInfo.name}`;
  };

  useEffect(() => {
    const fetchMembers = async () => {
      if (!channel) return;
      try {
        const Members = await ChannelService.getChannelMembers(channel.id);
        const mapMember = (member: any) => ({
          ...member,
          profile: {
            username: member.profile?.fullName || "",
            avatarUrl: member.profile?.profileImage || "",
            id: member.profile?.profileId || 0,
          },
        });
        setChannelMembers(Members.map(mapMember));
      } catch (err) {
        console.error("Error al cargar miembros:", err);
      }
    };
    fetchMembers();
  }, [channel]);

  const handleMemberAction = async (action: "promote" | "remove") => {
    if (!selectedMember || !channel) return;

    try {
      if (action === "promote") {
        const newRole =
          selectedMember.role === "Member" ? "Moderator" : "Member";
        await ChannelService.updateMemberRole({
          channelMemberId: selectedMember.id,
          role: newRole,
          profileId: currentUser.id,
          channelId: channel.id,
        });
      } else if (action === "remove") {
        await ChannelService.removeMember(channel.id, selectedMember.profileId);
      }
      const updatedMembers = await ChannelService.getChannelMembers(channel.id);
      setChannelMembers(updatedMembers);
    } catch (err: any) {
      alert(err.message || "Error al realizar la acción");
    } finally {
      setMenuAnchorEl(null);
      setSelectedMember(null);
      setSearchTerm("");
      setSearchResults([]);
    }
  };

  const handleInvite = async (user: ProfileDto) => {
    if (!channel) return;

    try {
      await ChannelService.inviteToChannel(channel.id, {
        invitedProfileId: user.userProfileId,
        invitedByProfileId: currentUser.id,
        isRequestToJoin: false,
      });
      setSnackbar({
        open: true,
        message: `Invitación enviada a ${user.username}`,
        severity: "success",
      });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message:
          err.message ||
          "Este usuario ya tiene una invitación pendiente o pertenece al canal",
        severity: "error",
      });
    }
  };

  useEffect(() => {
    const handleIncomingChannelMessage = async (
      ChannelMessageDto: ChannelMessageDto
    ) => {
      if (isGlobal || !channel || ChannelMessageDto.channelId !== channel.id) {
        return;
      }
      const message: NormalizedMessage = {
        id: ChannelMessageDto.id,
        content: ChannelMessageDto.content,
        sentAt:
          typeof ChannelMessageDto.sentAt === "string"
            ? ChannelMessageDto.sentAt
            : new Date(ChannelMessageDto.sentAt).toISOString(),
        senderProfileId: ChannelMessageDto.senderProfileId,
        channelId: ChannelMessageDto.channelId,
        messageType: ChannelMessageDto.messageType,
        files: ChannelMessageDto.files,
        senderProfile: ChannelMessageDto.senderProfile
          ? {
              id: ChannelMessageDto.senderProfile.id,
              username:
                ChannelMessageDto.senderProfile.username ||
                "Usuario Desconocido",
              avatarUrl:
                ChannelMessageDto.senderProfile?.profileImage ||
                "/assets/images/profilePicture.png",
              roleName: ChannelMessageDto.senderProfile.roleName || "",
              phoneNumberContact:
                ChannelMessageDto.senderProfile.phoneNumberContact || "",
              phoneNumberVenom:
                ChannelMessageDto.senderProfile.phoneNumberVenom || "",
            }
          : undefined,
      };
      setLiveMessages((prev) => [...prev, message]);
    };

    const handleIncomingGlobalMessage = async (
      GlobalMessageDto: GlobalMessageDto
    ) => {
      if (!isGlobal) {
        return;
      }

      const message: NormalizedMessage = {
        id: GlobalMessageDto.id,
        content: GlobalMessageDto.content,
        sentAt:
          typeof GlobalMessageDto.sentAt === "string"
            ? GlobalMessageDto.sentAt
            : new Date(GlobalMessageDto.sentAt).toISOString(),
        senderProfileId: GlobalMessageDto.profileId,
        files: GlobalMessageDto.files?.map((file) => ({
          id: file.id,
          channelMessageId: file.channelMessageId,
          globalMessageId: file.globalMessageId,
          fileLink: file.fileLink,
        })),
        senderProfile: GlobalMessageDto.senderProfile
          ? {
              id: GlobalMessageDto.senderProfile.id,
              username:
                GlobalMessageDto.senderProfile.username ||
                "Usuario Desconocido",
              avatarUrl:
                GlobalMessageDto.senderProfile.avatarUrl ||
                "/assets/images/profilePicture.png",
              roleName: "",
              phoneNumber: GlobalMessageDto.senderProfile.phoneNumber || "",
            }
          : undefined,
      };

      if (channelInfo && channelInfo.id === 28) {
        const handleIncomingVenomMessage = async (
          ChannelMessageDto: ChannelMessageDto
        ) => {
          if (
            isGlobal ||
            !channel ||
            ChannelMessageDto.channelId !== channel.id
          ) {
            return;
          }
          const message: NormalizedMessage = {
            id: ChannelMessageDto.id,
            content: ChannelMessageDto.content,
            sentAt:
              typeof ChannelMessageDto.sentAt === "string"
                ? ChannelMessageDto.sentAt
                : new Date(ChannelMessageDto.sentAt).toISOString(),
            senderProfileId: ChannelMessageDto.senderProfileId,
            channelId: ChannelMessageDto.channelId,
            messageType: ChannelMessageDto.messageType,
            files: ChannelMessageDto.files,
            senderProfile: ChannelMessageDto.senderProfile
              ? {
                  id: ChannelMessageDto.senderProfile.id,
                  username:
                    ChannelMessageDto.senderProfile.username ||
                    "Usuario Desconocido",
                  avatarUrl:
                    ChannelMessageDto.senderProfile?.profileImage ||
                    "/assets/images/profilePicture.png",
                  roleName: ChannelMessageDto.senderProfile.roleName || "",
                }
              : undefined,
          };
          setLiveMessages((prev) => [...prev, message]);
        };

        onReceiveVenomMessage(handleIncomingVenomMessage);
      }

      setLiveMessages((prev) => [...prev, message]);
    };

    onReceiveChannelMessage(handleIncomingChannelMessage);
    onReceiveGlobalMessage(handleIncomingGlobalMessage);
  }, [channel, isGlobal, channelInfo]);

  useEffect(() => {
    const fetchInitialMessages = async () => {
      try {
        let msgs: any[] = [];

        if (isGlobal) {
          msgs = await ChannelService.getGlobalMessages();
        } else if (channel?.id && !isGlobal) {
          msgs = await ChannelService.getChannelMessages(channel.id);
        } else {
          return;
        }
        const normalizedMsg: NormalizedMessage[] = msgs.map((msg: any) => {
          const isCurrentUser = isGlobal
            ? msg.senderProfile?.id === currentUser.id
            : msg.senderProfileId === currentUser.id;

          return {
            id: msg.id,
            content: msg.content,
            sentAt:
              typeof msg.sentAt === "string"
                ? msg.sentAt
                : new Date(msg.sentAt).toISOString(),
            files: msg.files || [],
            senderProfileId: isGlobal
              ? msg.senderProfile?.id
              : msg.senderProfileId,
            senderProfile: isCurrentUser
              ? {
                  id: currentUser.id,
                  username: currentUser.username ?? "Usuario",
                  avatarUrl:
                    currentUser.avatarUrl ??
                    "/assets/images/profilePicture.png",
                  roleName: currentUser.roleName ?? "",
                }
              : msg.senderProfile
              ? {
                  id: msg.senderProfile.id ?? msg.senderProfile.profileId,
                  username: isGlobal
                    ? msg.senderProfile.username
                    : msg.senderProfile.fullName || "Usuario Desconocido",
                  avatarUrl: isGlobal
                    ? msg.senderProfile.profileImage
                      ? msg.senderProfile.profileImage
                      : "/assets/images/profilePicture.png"
                    : msg.senderProfile?.profileImage ||
                      "/assets/images/profilePicture.png",
                  roleName: msg.senderProfile.roleName ?? "",
                  phoneNumberContact:
                    msg.senderProfile.phoneNumberContact || "",
                  phoneNumber:
                    msg.senderProfile.phoneNumber || "",
                  phoneNumberVenom:
                    msg.senderProfile.phoneNumberVenom || "",
                }
              : undefined,
            channelId: msg.channelId ?? undefined,
            messageType: msg.messageType ?? "texto",
          };
        });
        setInitialMessages(normalizedMsg);
      } catch (error) {
        console.error("Error al cargar mensajes:", error);
      }
    };

    fetchInitialMessages();
  }, [channel, isGlobal, currentUser]);

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const IsActiveMember = (channelMembers || []).some(
    (c) => c.profileId === currentUser.id
  );

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const autoScrollPausedRef = useRef(false);

  useEffect(() => {
    if (!isGlobal) return;
    const container = messagesContainerRef.current;
    if (!container) return;

    let animationFrameId: number;

    const scrollStep = 1.5;

    const scroll = () => {
      if (!container || autoScrollPausedRef.current) {
        animationFrameId = requestAnimationFrame(scroll);
        return;
      }

      const maxScroll = container.scrollHeight - container.clientHeight;

      if (container.scrollTop < maxScroll) {
        container.scrollTop += scrollStep;
      } else {
        container.scrollTop = 0;
      }

      animationFrameId = requestAnimationFrame(scroll);
    };

    animationFrameId = requestAnimationFrame(scroll);

    // Eventos para pausar y reanudar
    const pauseScroll = () => {
      autoScrollPausedRef.current = true;
    };

    const resumeScroll = () => {
      autoScrollPausedRef.current = false;
    };

    const containerEl = container;

    containerEl.addEventListener("mouseenter", pauseScroll);
    containerEl.addEventListener("mouseleave", resumeScroll);
    containerEl.addEventListener("wheel", pauseScroll);

    return () => {
      cancelAnimationFrame(animationFrameId);
      containerEl.removeEventListener("mouseenter", pauseScroll);
      containerEl.removeEventListener("mouseleave", resumeScroll);
      containerEl.removeEventListener("wheel", pauseScroll);
    };
  }, [allMessages.length, isGlobal]);

  const onPromote = async (member: ChannelMemberDto, role: string) => {
    try {
      const res = await ChannelService.updateMemberRole({
        channelId: channel?.id || 0,
        profileId: member.profileId,
        channelMemberId: member.id,
        role,
      });
      if (res?.success) {
        setChannelMembers((prev) =>
          prev.map((m) => (m.id === member.id ? { ...m, role } : m))
        );
        setSnackbar({
          open: true,
          message: res.message,
          severity: "success",
        });
      }
    } catch (error: any) {
      console.error("Error al promover miembro:", error);
      setSnackbar({
        open: true,
        message: error.message,
        severity: "error",
      });
    }
  };

  const onRemove = async (member: ChannelMemberDto) => {
    try {
      const res = await ChannelService.removeMember(
        member.channelId,
        member.profileId
      );
      if (res?.success) {
        setChannelMembers((prev) => prev.filter((m) => m.id !== member.id));
        setSnackbar({
          open: true,
          message: res.message,
          severity: "success",
        });
      }
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: "error",
      });
    }
  };

  const onLeaveChannel = async () => {
    try {
      const res = await ChannelService.leaveChannel(channel?.id || 0, {
        profileId: currentUser.userProfileId,
      });
      if (res?.success) {
        if (fetchChannels) {
          await fetchChannels();
        }
        if (fetchPublicChannels) {
          await fetchPublicChannels();
        }
        if (setSelectedChannel) {
          setSelectedChannel(null);
        }
        if (setSelectedTab) {
          setSelectedTab("channels");
        }
        setSnackbar({
          open: true,
          message: res.message,
          severity: "success",
        });
      }
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: "error",
      });
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        overflow: "hidden",
        pl: 1,
        height: "100%",
      }}
    >
      {/* Header */}
      <HeaderMessageBody
        isGlobal={isGlobal}
        currentUser={currentUser}
        handleShowChats={handleShowChats}
        setOpenChannelDetails={setOpenChannelDetails}
        setEditDialogOpen={setEditDialogOpen}
        getChannelTitle={getChannelTitle}
        getChannelIcon={getChannelIcon}
        IsActiveMember={IsActiveMember}
        filter={filter}
        setFilter={setFilter}
        isAdmin={isAdmin}
        channel={channel}
        channelMembers={channelMembers}
        setMembersDialogOpen={setMembersDialogOpen}
        channelInfo={channelInfo}
      />

      <>
        {IsActiveMember || isGlobal ? (
          <>
            <Box
              ref={messagesContainerRef}
              sx={{
                flexGrow: 1,
                overflowY: "auto",
                p: 3,
                display: "flex",
                flexDirection: "column",
                gap: 3,
                bgcolor: "background.default",
                scrollBehavior: isGlobal ? "auto" : "smooth",
              }}
            >
              {allMessages.length === 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  {getChannelIcon()}
                  <Typography
                    color="text.secondary"
                    variant="h6"
                    sx={{ fontWeight: 500 }}
                  >
                    {isGlobal
                      ? "No hay mensajes globales aún"
                      : channel
                      ? `Bienvenido a # ${channel.name}`
                      : "Selecciona un canal para comenzar"}
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    {isGlobal
                      ? "¡Sé el primero en enviar un mensaje global!"
                      : channel
                      ? "Este es el comienzo de tu conversación en este canal"
                      : "Elige un canal de la barra lateral para ver los mensajes"}
                  </Typography>
                </Box>
              ) : (
                allMessages.map((message, index) => {
                  const sender = message.senderProfile;
                  const senderId = isGlobal
                    ? message.senderProfile?.id
                    : message.senderProfileId;
                  const prevMessage = index > 0 ? allMessages[index - 1] : null;
                  const prevSenderId =
                    prevMessage &&
                    (isGlobal
                      ? prevMessage.senderProfile?.id
                      : prevMessage.senderProfileId);
                  const isCurrentUser = isGlobal
                    ? message.senderProfile?.id === currentUser.id
                    : message.senderProfileId === currentUser.id;
                  const showAvatar = !prevMessage || prevSenderId !== senderId;
                  return (
                    <Box
                      key={message.id}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 2,
                        "&:hover .message-actions": {
                          opacity: 1,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        {showAvatar ? (
                          <Avatar
                            src={
                              sender?.avatarUrl ||
                              "/placeholder.svg?height=40&width=40"
                            }
                            alt={sender?.username || "User"}
                            sx={{ width: 36, height: 36 }}
                          />
                        ) : (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              fontSize: "0.6875rem",
                              mt: 0.5,
                              opacity: 0,
                              "&:hover": { opacity: 1 },
                            }}
                          >
                            {formatTime(message.sentAt)}
                          </Typography>
                        )}
                      </Box>

                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        {showAvatar && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "baseline",
                              gap: 1,
                              mb: 0.5,
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 600,
                                color: "green",
                              }}
                            >
                              {isCurrentUser
                                ? `${sender?.username} (Tú)`
                                : !sender?.username
                                ? "Usuario Desconocido"
                                : sender.username}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {formatTime(message.sentAt)}
                            </Typography>
                          </Box>
                        )}
                        <Paper
                          elevation={0}
                          sx={{
                            p: 1,
                            bgcolor: isCurrentUser ? "#308a46" : "lightgrey",
                            color: isCurrentUser ? "background.paper" : "black",
                            borderRadius: 2,
                            border: "1px solid",
                            borderColor: isCurrentUser
                              ? "#308a46"
                              : "lightgrey",
                            display: "inline-block",
                            width: { xs: "80%", md: "60%" },
                            wordBreak: "break-word",
                          }}
                        >
                          {message.files && message.files.length > 0 && (
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 1,
                                mb: 1,
                              }}
                            >
                              {message.files
                                .slice(0, 5)
                                .map((file, fileIndex) => {
                                  const fileUrl = file.fileLink;
                                  const isVideo =
                                    fileUrl.match(/\.(mp4|webm|ogg)$/i);
                                  const showOverlay =
                                    fileIndex === 4 &&
                                    (message.files?.length ?? 0) > 5;

                                  return (
                                    <Box
                                      key={fileUrl}
                                      onClick={() => {
                                        setActivePreviewFileIndex(fileIndex);
                                        setActivePreviewFiles(
                                          message.files ?? []
                                        );
                                        setPreviewDialogOpen(true);
                                      }}
                                      sx={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "contain",
                                        position: "relative",
                                        cursor: "pointer",
                                        borderRadius: 2,
                                        overflow: "hidden",
                                        border: "1px solid #ccc",
                                        bgcolor: "grey.100",
                                      }}
                                    >
                                      {isVideo ? (
                                        <video
                                          src={fileUrl}
                                          muted
                                          style={{
                                            width: "100%",
                                            height: "100%",
                                          }}
                                        />
                                      ) : (
                                        <img
                                          src={fileUrl}
                                          alt={`preview-${fileIndex}`}
                                          style={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                          }}
                                        />
                                      )}
                                      {showOverlay && (
                                        <Box
                                          sx={{
                                            position: "absolute",
                                            top: 0,
                                            left: 0,
                                            width: "100%",
                                            height: "100%",
                                            bgcolor: "rgba(0,0,0,0.6)",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            color: "white",
                                            fontSize: "1rem",
                                            fontWeight: "bold",
                                          }}
                                        >
                                          +
                                          {message.files &&
                                            message.files.length - 5}
                                        </Box>
                                      )}
                                    </Box>
                                  );
                                })}
                            </Box>
                          )}
                          <Typography
                            variant="body1"
                            sx={{
                              wordBreak: "break-word",
                              fontSize: { xs: "14px", md: "15px" },
                            }}
                          >
                            {message.content.split(/(\s+)/).map((word, idx) =>
                              word.startsWith("@") ? (
                                <Box
                                  component="span"
                                  key={idx}
                                  sx={{
                                    color: !isCurrentUser
                                      ? "primary.main"
                                      : "text.secondary",
                                    fontWeight: 600,
                                  }}
                                >
                                  {word}
                                </Box>
                              ) : (
                                word
                              )
                            )}
                          </Typography>
                        </Paper>
                      </Box>

                      <MessageActionsMenu
                        message={message}
                        isCurrentUser={isCurrentUser}
                        senderWhatsApp={isGlobal ? sender?.phoneNumber : sender?.phoneNumberVenom ? sender?.phoneNumberVenom : sender?.phoneNumberContact} 
                      />
                    </Box>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </Box>
            <MessageInput
              message={newMessage}
              setMessage={setNewMessage}
              handleSend={handleSendMessage}
              isLoading={isLoading}
              files={files}
              setFiles={setFiles}
              channelMembers={channelMembers}
              currentUserId={currentUser.id}
            />
          </>
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexGrow: 1,
              p: 3,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {!IsActiveMember && channel
                ? "No eres miembro activo de este canal. Únete para participar."
                : "Selecciona un canal para enviar mensajes."}
            </Typography>
          </Box>
        )}
      </>

      {/* Members Dialog */}
      <MembersDialog
        membersDialogOpen={membersDialogOpen}
        setMembersDialogOpen={setMembersDialogOpen}
        isAdmin={isAdmin}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        searchResults={searchResults}
        setSearchResults={setSearchResults}
        channelMembers={channelMembers}
        handleInvite={handleInvite}
        handleMemberAction={handleMemberAction}
        selectedMember={selectedMember}
        menuAnchorEl={menuAnchorEl}
        setMenuAnchorEl={setMenuAnchorEl}
      />
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      {channel && (
        <>
          <ChannelDetailsDialog
            open={openChannelDetails}
            onClose={() => setOpenChannelDetails(false)}
            channel={channelInfo}
            members={channelMembers}
            currentUser={currentUser}
            activeUserIds={
              activeUserIds && activeUserIds.length > 0 ? activeUserIds : []
            }
            onPromote={onPromote}
            onRemove={onRemove}
            onLeaveChannel={onLeaveChannel}
          />
          <EditChannel
            open={editDialogOpen}
            onClose={() => setEditDialogOpen(false)}
            channelId={channelInfo?.id ?? 0}
            initialName={channelInfo?.name ?? ""}
            initialDescription={channelInfo?.description ?? ""}
            initialIsPublic={channelInfo?.isPublic ?? false}
            initialRequiresInvite={channelInfo?.requiresInvite ?? false}
            initialImage={channelInfo?.imageUrl ?? ""}
            initialWhatsappLink={channelInfo?.whatsappLink ?? ""}
            onUpdated={async () => {
              const updated = await ChannelService.getChannel(
                channelInfo?.id ?? 0
              );
              if (updated.success) {
                setChannelInfo(updated.data);
              }
            }}
            fetchChannels={fetchChannels}
            fetchPublicChannels={fetchPublicChannels}
            setSelectedTab={setSelectedTab}
            setSelectedChannel={setSelectedChannel}
          />
          <Dialog
            open={previewDialogOpen}
            onClose={() => setPreviewDialogOpen(false)}
            sx={{ width: "100vw", height: "100vh" }}
            fullWidth
          >
            <Box sx={{ p: 2, textAlign: "center", position: "relative" }}>
              <IconButton
                onClick={() =>
                  setActivePreviewFileIndex((i) => Math.max(0, i - 1))
                }
                disabled={activePreviewFileIndex === 0}
                sx={{ position: "absolute", top: "50%", left: 16 }}
              >
                <ArrowLeft sx={{ fontSize: 80 }} />
              </IconButton>

              {activePreviewFiles[activePreviewFileIndex]?.fileLink?.match(
                /\.(mp4|webm|ogg)$/i
              ) ? (
                <video
                  src={activePreviewFiles[activePreviewFileIndex]?.fileLink}
                  controls
                  style={{ maxHeight: "70vh", maxWidth: "100%" }}
                />
              ) : (
                <img
                  src={activePreviewFiles[activePreviewFileIndex]?.fileLink}
                  alt="preview"
                  style={{ maxHeight: "70vh", maxWidth: "100%" }}
                />
              )}

              <IconButton
                onClick={() =>
                  setActivePreviewFileIndex((i) =>
                    Math.min(activePreviewFiles.length - 1, i + 1)
                  )
                }
                disabled={
                  activePreviewFileIndex === activePreviewFiles.length - 1
                }
                sx={{ position: "absolute", top: "50%", right: 16 }}
              >
                <ArrowRight sx={{ fontSize: 80 }} />
              </IconButton>
            </Box>
          </Dialog>
        </>
      )}
    </Box>
  );
}
